/**
 * Public types of the H5P translation semantics walker (Multi-Language
 * Trainings). The walker is a pure, dependency-free module that
 * pairs a library's `semantics.json` with content params and derives the
 * translatable units, keyed by the ticket-1 stable-id contract
 * (`subContentId ?? id` for list/sub-content items, group-name paths for
 * module-level fields).
 *
 * IMPORTANT: no Angular / jQuery / app imports here — the module is consumed
 * by the editor UI, the runtime player merge, progress counters and the
 * reporting overlay alike.
 */

/** The four kinds of translatable units the walker emits. */
export const TRANSLATABLE_FIELD_TYPES = {
  TEXT: 'text',
  HTML: 'html',
  IMAGE: 'image',
  MEDIA: 'media',
} as const;

export type TranslatableFieldType =
  (typeof TRANSLATABLE_FIELD_TYPES)[keyof typeof TRANSLATABLE_FIELD_TYPES];

/** The only `translatableCondition` value in use (conditional fields). */
export const TRANSLATABLE_CONDITION_SOURCE_NON_EMPTY = 'sourceNonEmpty';

/**
 * `translatableAs` marker values. `'url'` (Interactive Video `video/files`)
 * means the field is translatable ONLY when its source value is an external
 * video URL (YouTube/Vimeo/Panopto/direct link); an uploaded local file source
 * stays universal (subtitle tracks are not per-language). The translated value
 * keeps the same file-object-array shape as the source (`[{ path, mime }]`).
 */
export const TRANSLATABLE_AS_URL = 'url';

/**
 * One field definition of a `semantics.json` file. Semantics are foreign JSON
 * — only the attributes the walker reads are declared, everything else is
 * carried through the index signature.
 */
export interface H5PSemanticsField {
  readonly name?: string;
  readonly type?: string;
  readonly label?: string;
  readonly entity?: string;
  readonly widget?: string;
  readonly common?: boolean;
  readonly translatable?: boolean;
  readonly translatableCondition?: string;
  /** Inert marker; `'url'` = translatable only when the source is a URL. */
  readonly translatableAs?: string;
  readonly isSubContent?: boolean;
  /** List-of-text -> list-of-group migration marker (SortParagraphs). */
  readonly stringItemField?: string;
  /** Sub-fields of a `group`. */
  readonly fields?: readonly H5PSemanticsField[];
  /** Item definition of a `list`. */
  readonly field?: H5PSemanticsField;
  /** Allowed libraries of a `library` field (uber names). */
  readonly options?: readonly unknown[];
  readonly [key: string]: unknown;
}

/** A whole semantics.json (the top-level array of field definitions). */
export type H5PSemantics = readonly H5PSemanticsField[];

/** Content params — foreign JSON, structure mirrors semantics storage. */
export type H5PParams = Record<string, unknown>;

/**
 * An H5P file object as stored in params for image/file/video/audio fields
 * (e.g. `{ path, mime, width, height, copyright }`).
 */
export interface H5PFileObject {
  readonly path?: string;
  readonly mime?: string;
  readonly [key: string]: unknown;
}

/**
 * A value stored in the flat translation map:
 * - `'text'` / `'html'` units: the translated string.
 * - `'image'` / `'media'` units on plain file fields (semantics type
 *   `image`/`file`): the whole H5P file object — merge swaps the entire
 *   object.
 * - `'media'` units on `video`/`audio` fields (e.g. Audio `files`,
 *   InteractiveVideo `video/files`): the whole file-object ARRAY.
 * - `'image'` / `'media'` units backed by an embedded `H5P.Image`/`H5P.Video`
 *   library (MultiChoice `media`, feedback images, IV/ImageHotspots `action`):
 *   the embedded library's entire `params` object (for `H5P.Image` that is
 *   `{ file, alt?, title? }`, for `H5P.Video` `{ sources, ... }`). Merge swaps
 *   only the embed's `params`, keeping `library` and `subContentId` from the
 *   source.
 */
export type TranslationValue =
  | string
  | Readonly<Record<string, unknown>>
  | ReadonlyArray<Readonly<Record<string, unknown>>>;

/**
 * Flat translation map for one target language:
 * `"<itemKey>:<fieldName>" -> value`.
 */
export type TranslationMap = Readonly<Record<string, TranslationValue>>;

/**
 * Resolves the semantics of an embedded library by its uber name (e.g.
 * `'H5P.MultiChoice 1.14'`). Return `undefined` for unknown libraries — the
 * walker then skips that subtree gracefully.
 */
export type SemanticsResolverFn = (
  uberName: string,
) => H5PSemantics | undefined;

/**
 * Sub-library semantics source: either a resolver function or a preloaded
 * record keyed by uber name (`'H5P.MultiChoice 1.14'`); record lookups fall
 * back to the bare machine name (`'H5P.MultiChoice'`).
 */
export type SemanticsResolver =
  | SemanticsResolverFn
  | Readonly<Record<string, H5PSemantics>>;

export interface WalkerOptions {
  /** Semantics for embedded libraries. Omit to skip all embeds. */
  readonly resolveSemantics?: SemanticsResolver;
}

export interface MergeOptions extends WalkerOptions {
  /**
   * Target language code. Reserved for API stability — the current merge does
   * not stamp it anywhere in params (no defined H5P semantics for it yet).
   */
  readonly language?: string;
}

/** Info about the item (question / list entry / page group) owning a field. */
export interface TranslatableItemInfo {
  /**
   * The owning item's stable key (`subContentId ?? id`), the group-name path
   * for module-level fields (e.g. `'wizard/introPage'`), or `null` for items
   * that cannot be keyed yet (legacy items without minted ids).
   */
  readonly itemKey: string | null;
  /** Human label of the owning item/group (for editor grouping). */
  readonly label: string;
  /** Uber name of the library instance the item belongs to (embeds only). */
  readonly library?: string;
  /** Index of the item within its list, when it is a list item. */
  readonly index?: number;
  /** Absolute params path of the owning item. */
  readonly path: string;
}

/** One translatable unit, in stable document order. */
export interface TranslatableField {
  /** Same as `item.itemKey` (convenience). */
  readonly itemKey: string | null;
  /**
   * Field name relative to the owning item — a single name (`'text'`) or a
   * slash path for fields nested in plain groups
   * (`'tipsAndFeedback/notChosenFeedback'`).
   */
  readonly fieldName: string;
  /** `"<itemKey>:<fieldName>"`, or `null` when the item cannot be keyed. */
  readonly mapKey: string | null;
  /** Absolute human-readable params path, e.g. `'wizard/questions[0]/params/question'`. */
  readonly path: string;
  /**
   * Programmatic path of the unit's value inside params (string keys and
   * array indices). For library-backed image/media units it points at the
   * embed's `params` key.
   */
  readonly pathSegments: ReadonlyArray<string | number>;
  /** Field label from semantics (falls back to the field name). */
  readonly label: string;
  readonly type: TranslatableFieldType;
  /** Current source-language value (may be `undefined` when unset). */
  readonly sourceValue: TranslationValue | undefined;
  /** True when `translatableCondition: 'sourceNonEmpty'` applies. */
  readonly conditional: boolean;
  /**
   * True for image/media units backed by an embedded `H5P.Image`/`H5P.Video`
   * library; `embedLibrary` then carries the embed's uber name.
   */
  readonly libraryAsset: boolean;
  readonly embedLibrary?: string;
  /**
   * Stable keys of all keyed ancestors, outermost first, ending with the
   * owning item's key (module-path key for module-level fields). Used by
   * progress counters to attribute a field to every enclosing item.
   */
  readonly keyChain: readonly string[];
  readonly item: TranslatableItemInfo;
}

export interface ProgressCount {
  readonly translated: number;
  readonly total: number;
}

export interface TranslationProgress extends ProgressCount {
  /**
   * Per-item subtotals keyed by item key. Entries overlap hierarchically —
   * a nested item's fields are also counted in every enclosing item's entry
   * (a QuestionSet question's entry includes its answers and embedded
   * sub-content) — so the module `translated`/`total` are NOT the sum of
   * `perItem`, they count every field exactly once. Hard-excluded items
   * (Drag & Drop) get an explicit `0/0` entry.
   */
  readonly perItem: Readonly<Record<string, ProgressCount>>;
}

export interface MintResult {
  /** Deep copy of the input params with the missing ids assigned. */
  readonly params: H5PParams;
  /** Whether any id was minted (or legacy item restructured). */
  readonly changed: boolean;
}
